import { Box, Grid, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import TitleSubTitle from "../SchemaComponents/TitleSubtitle/TitleSubTitle";

interface SlugFieldProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  // Omit to hide the read-only preview row entirely (e.g. collections, which have
  // no public/rendered URL).
  canonicalUrl?: string;
  layout?: "row" | "column";
}

export const SlugFieldLabel = () => {
  const { t } = useTranslation();
  return (
    <TitleSubTitle
      title={`${t("slug_field_label")} *`}
      subTitle={t("slug_field_description")}
      titleVariant='h6medium'
      subTitleVariant='p4regular'
      titleId='slug_label'
    />
  );
};

export const SlugFieldInput = ({
  value,
  onChange,
  onBlur,
  disabled = false,
}: Omit<SlugFieldProps, "layout" | "canonicalUrl">) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ width: "100%" }}>
      <TextField
        variant='filled'
        size='small'
        fullWidth
        name='slug'
        id='slug'
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={t("slug_placeholder")}
        inputProps={{ "aria-labelledby": "slug_label" }}
      />
    </Box>
  );
};

export const CanonicalFieldLabel = () => {
  const { t } = useTranslation();
  return (
    <TitleSubTitle
      title={t("canonical_field_label")}
      subTitle={t("canonical_field_description")}
      titleVariant='h6medium'
      subTitleVariant='p4regular'
      titleId='canonical_label'
    />
  );
};

export const CanonicalFieldInput = ({ canonicalUrl = "" }: { canonicalUrl?: string }) => {
  return (
    <Box sx={{ width: "100%" }}>
      <TextField
        variant='filled'
        size='small'
        fullWidth
        multiline
        disabled
        name='canonical_url'
        id='canonical_url'
        value={canonicalUrl}
        inputProps={{
          "aria-labelledby": "canonical_label",
          "data-testid": "canonical-url-preview",
        }}
      />
    </Box>
  );
};

const SlugField = ({
  value,
  onChange,
  onBlur,
  disabled = false,
  canonicalUrl,
  layout = "row",
}: SlugFieldProps) => {
  if (layout === "column") {
    return (
      <Box id='slug_id'>
        <SlugFieldLabel />
        <Box sx={{ marginTop: "8px" }}>
          <SlugFieldInput value={value} onChange={onChange} onBlur={onBlur} disabled={disabled} />
        </Box>
        {canonicalUrl && (
          <>
            <Box sx={{ marginTop: "40px" }}>
              <CanonicalFieldLabel />
            </Box>
            <Box sx={{ marginTop: "8px" }}>
              <CanonicalFieldInput canonicalUrl={canonicalUrl} />
            </Box>
          </>
        )}
      </Box>
    );
  }

  const leftColSx = {
    paddingRight: {
      xs: 0,
      sm: 0,
      md: 0,
      lg: "55px",
    },
  };

  return (
    <Box id='slug_id'>
      <Grid container rowSpacing={1}>
        <Grid item xs={12} sm={12} md={12} lg={5} sx={leftColSx}>
          <SlugFieldLabel />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={7}>
          <SlugFieldInput value={value} onChange={onChange} onBlur={onBlur} disabled={disabled} />
        </Grid>
        {canonicalUrl && (
          <>
            <Grid item xs={12} sm={12} md={12} lg={5} sx={{ ...leftColSx, marginTop: "32px" }}>
              <CanonicalFieldLabel />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={7} sx={{ marginTop: { lg: "32px" } }}>
              <CanonicalFieldInput canonicalUrl={canonicalUrl} />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default SlugField;
